import React, { useState, useEffect } from "react";
import { toast } from "../../utils/toast";
import Icon from "../../components/em/Icon";
import PropTypes from "prop-types";
import axios from "axios";
import { API_URL } from "../../api/api";



const RoleModal = ({ isOpen, onClose, roleData, permissionsData, onSave }) => {
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#3B82F6");
  const [loading, setLoading] = useState(false);

  const baseURL = API_URL;

  // Admin is the superuser: it holds every permission in the catalog, always,
  // and the server re-asserts that on save. Showing it as editable would let
  // someone untick boxes that silently come back — so the grid is locked here
  // and rendered fully ticked instead.
  const isAdminRole =
    String(roleData?.name || "").trim().toLowerCase() === "admin";

  const allPermissions = permissionsData.flatMap((group) =>
    group.items.map((item) => item.key)
  );

  useEffect(() => {
    if (roleData) {
      setRoleName(roleData.name || "");
      setDescription(roleData.description || "");
      setColor(roleData.color || "#3B82F6");
      const isAdmin =
        String(roleData.name || "").trim().toLowerCase() === "admin";
      setSelectedPermissions(
        isAdmin
          ? permissionsData.flatMap((g) => g.items.map((i) => i.key))
          : roleData.permissions || []
      );
    } else {
      setRoleName("");
      setDescription("");
      setColor("#3B82F6");
      setSelectedPermissions([]);
    }
  }, [roleData, isOpen, permissionsData]);

  if (!isOpen) return null;

  const isAllSelected =
    allPermissions.length > 0 &&
    allPermissions.every((key) => selectedPermissions.includes(key));

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key)
        ? prev.filter((p) => p !== key)
        : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    setSelectedPermissions(allPermissions);
  };

  const handleDeselectAll = () => {
    setSelectedPermissions([]);
  };

  const handleGroupSelectAll = (items) => {
    const groupKeys = items.map((item) => item.key);

    setSelectedPermissions((prev) => {
      const merged = new Set([...prev, ...groupKeys]);
      return Array.from(merged);
    });
  };

  const handleGroupDeselectAll = (items) => {
    const groupKeys = items.map((item) => item.key);

    setSelectedPermissions((prev) =>
      prev.filter((key) => !groupKeys.includes(key))
    );
  };

  const isGroupAllSelected = (items) => {
    const groupKeys = items.map((item) => item.key);
    return (
      groupKeys.length > 0 &&
      groupKeys.every((key) => selectedPermissions.includes(key))
    );
  };

  const handleSave = async () => {
  if (!roleName.trim()) {
    toast.error("Role name is required");
    return;
  }

  const uniquePermissions = [...new Set(selectedPermissions)];

  const payload = {
    id: roleData?.id || null,
    name: roleName.trim(),
    description: description.trim(),
    color,
    permissions: uniquePermissions,
  };

  try {
    setLoading(true);

    await axios.post(`${baseURL}/api/roles/save`, payload);

    if (onSave) {
      onSave(payload);
    }

    toast.success(`Role ${roleData ? "updated" : "created"} successfully`);
    onClose();
  } catch (err) {
    console.error("Save role error:", err);
    toast.error(err?.response?.data?.error || "Failed to save role");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="modal-overlay">
      <div className="role-modal-container modal">
        <div className="modal-header">
          <div className="role-title">
            <h3>{roleData ? "Edit Role" : "Create Role"}</h3>
            <p>Define the role name, description and assign permissions</p>
          </div>
          <div className="close-btn" onClick={onClose}>✕</div>
        </div>

        <div className="role-list-wrapp">
          <div className="role-form-group">
            <div className="form-item">
              <label>Role Name</label>
              <input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="Enter role name"
                disabled={isAdminRole}
              />
            </div>

            <div className="form-item">
              <label>Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this role"
              />
            </div>

            <div className="form-item">
              <label>Color</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
            </div>
          </div>

          <div className="permissions-section">

            {isAdminRole && (
              <div
                style={{
                  padding: "12px 14px",
                  marginBottom: 12,
                  borderRadius: 10,
                  background: "linear-gradient(135deg,#eff6ff,#f0f9ff)",
                  border: "1px solid #bfdbfe",
                  font: "400 13px/1.5 inherit",
                  color: "#1e3a5f",
                }}
              >
                <b>Admin has full access to the entire application.</b> Its
                permissions are fixed and cannot be changed — this is what
                guarantees the organisation can never be locked out of its own
                administration. Every other role, including HR, is granted
                access from this screen.
              </div>
            )}

            {permissionsData.map((group) => {
              const groupAllSelected = isGroupAllSelected(group.items);
              const groupSelectedCount = group.items.filter((it) =>
                selectedPermissions.includes(it.key)
              ).length;

              return (
                <div key={group.module} className="permission-group">
                  <div className="group-title">
                    <h4>
                      <i>
                        <Icon name={group.icon} color="#F54900" />
                      </i>
                      {group.module}
                      <span className="group-count">
                        {groupSelectedCount}/{group.items.length}
                      </span>
                    </h4>

                    {!isAdminRole && (
                      <div className="group-actions">
                        {!groupAllSelected ? (
                          <button
                            type="button"
                            onClick={() => handleGroupSelectAll(group.items)}
                          >
                            Select All
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleGroupDeselectAll(group.items)}
                          >
                            Deselect All
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="permission-wrap">
                    {group.items.map((perm) => (
                      <div key={perm.key} className="permission-item">
                        <div className="inner">
                          <div className="check-btn">
                            <label className="ckbox">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(perm.key)}
                                onChange={() => togglePermission(perm.key)}
                                disabled={isAdminRole}
                              />
                              <i></i>
                              <div className="text-wrap">
                                <p><b>{perm.name}</b></p>
                                <p className="desc">
                                  {perm.description || "Permission access for this feature."}
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="role-modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="org-gradi" onClick={handleSave} disabled={loading}>
            <Icon name="save" /> {loading ? "Saving..." : "Save Role"}
          </button>
        </div>
      </div>
    </div>
  );
};



RoleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roleData: PropTypes.object,
  permissionsData: PropTypes.array.isRequired,
  onSave: PropTypes.func,
};

export default RoleModal;